import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Logger, LoggerFactory } from '../../../core/logger.service';
import {
  AccountCustomFont,
  AccountCustomFontType,
  AccountVariable,
  AccountVariableType,
} from '../../account-settings-models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BrokenPageDialogComponent } from '../../../shared/broken-page-dialog/broken-page-dialog.component';
import { IndeterminateProgressDialogComponent } from '../../../shared/progress/indeterminate-progress-dialog/indeterminate-progress-dialog.component';
import { AccountSettingsService } from '../../account-settings.service';
import { SnackBarService } from '../../../core/snack-bar.service';
import ColorHelpers from 'app/shared/helpers/color-helpers';
import { TranslateService } from '@ngx-translate/core';
import { FlyoutDataType, FlyoutOpeningData } from 'app/flyout/flyout-models';
import { FlyoutService } from 'app/flyout/flyout.service';
import { Subscription } from 'rxjs';
import { BrandVariablesService } from 'app/account/brand-variables.service';
import { BrandVariable, BrandVariableBorder, BrandVariableTextProperties } from 'app/account/brand-variable';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { WidgetFontDescription } from 'app/campaign/widgets.model';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { MatTabGroup } from '@angular/material/tabs';
import { CookieService } from 'app/core/cookie.service';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { UserModel } from 'app/account-settings/models/user.model';
import { ConfirmationDialogService } from 'app/shared/confirmation-dialog/confirmation-dialog.service';
import { ApplicationStarterService } from 'app/core/application-starter.service';

export interface AccountVariableFont extends AccountVariable {
  isCustom?: boolean;
  isDefault?: boolean;
  fontType?: AccountCustomFontType;
}

@Component({
  selector: 'app-account-settings-brand-settings',
  templateUrl: './account-settings-brand-settings.component.html',
  styleUrls: ['./account-settings-brand-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountSettingsBrandSettingsComponent implements OnInit, OnDestroy {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  @ViewChildren('inputRef') inputs!: QueryList<ElementRef<HTMLInputElement>>;
  @Output() onChangeBrandVariables = new EventEmitter();
  @Output() onSaveBrandVariables = new EventEmitter();
  @Input() isBrandVarPageForTemplatePage: boolean;
  @Input() customer: CustomerModel;
  @Input() user: UserModel;
  public sanitizedSupportedFonts: Array<SafeResourceUrl>;
  public brandVariablesPreview: BrandVariable;

  private readonly logger: Logger;
  public loadingCustomFonts = false;
  public customFonts: Array<AccountCustomFont>;
  public defaultFonts: Array<WidgetFontDescription>;
  private brokenPageDialogRef: MatDialogRef<BrokenPageDialogComponent>;
  private progressDialogRef: MatDialogRef<IndeterminateProgressDialogComponent>;
  public AccountVariableTypes = AccountVariableType;
  public AccountCustomFontTypes = AccountCustomFontType;
  public sanitizedSupportedCssFonts: Array<SafeResourceUrl>;
  public uploadedFonts: Array<AccountCustomFont>;

  public loadingAccountVariables = false;
  public allAccountVariables: Array<AccountVariable>;
  public accountColors: Array<AccountVariable>;
  public accountFonts: Array<AccountVariableFont>;
  public accountTextVariables: Array<AccountVariable>;
  public lastAddedColor = [];
  private colorBlurHandled = false;
  private colorBlurTimeout: any = null;

  public initialAccountVariables: Array<AccountVariable>;
  public savingColorInProgress: boolean;
  public errorKeyColor: string;
  public errorValueColor: string;
  public newColor = null;

  public brandVariables: BrandVariable;
  public isAutomaticallyGenerated: boolean;
  public initialBrandVariables: BrandVariable;
  public prevTab: { index: number; key: string };
  public selectedTab: number;
  public brandVarCanBeSavedOnClickTab: { brandVariablesErrors: any; isBrandSettingsNotChanged: boolean };
  public TabsBrandSettings = TabsBrandSettings;
  public loadingBrandVariables = true;

  public savingTextInProgress: boolean;
  public errorValueText: string;
  public errorKeyText: string;
  public textValueRegex = RegExp('^([a-zA-Z0-9äöåÄÖÅ\\-._~:/?#\\[\\]@!$&\'"()*+,;%=\\s]+)$');
  public newText = null;
  public flyoutSubscription: Subscription;
  public verifyEmailMessageSubscription: Subscription;
  constructor(
    protected readonly loggerFactory: LoggerFactory,
    private accountSettingService: AccountSettingsService,
    public sanitizer: DomSanitizer,
    private snackbarService: SnackBarService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private flyoutService: FlyoutService,
    private brandVariablesService: BrandVariablesService,
    private widgetEditorDataService: WidgetEditorDataService,
    private cookieService: CookieService,
    private confirmationDialogService: ConfirmationDialogService,
  ) {
    this.logger = loggerFactory.get('AccountSettingsBrandSettingsComponent');
  }

  public ngOnInit() {
    this.loadDataForCustomization();
    this.flyoutSubscription = this.flyoutService.flyoutUpdatedBrandSettings.subscribe((data) => {
      if (data && !data.isEdit) {
        this.customFonts.push(data.customFont);
        if (!!data.customFont.cssLink && data.customFont.cssLink.length > 0) {
          this.sanitizedSupportedCssFonts.push(this.sanitizer.bypassSecurityTrustResourceUrl(data.customFont.cssLink));
        }
        if (!!data.customFont.fontFiles && data.customFont.fontFiles.length > 0) {
          this.uploadedFonts.push(data.customFont);
          this.injectAllUploadedFonts(this.uploadedFonts);
        }
      } else if (data && data.isEdit) {
        const idxCustomFont = this.customFonts.findIndex((font) => font.id === data.customFont.id);
        this.customFonts[idxCustomFont] = data.customFont;
      }
    });
  }

  private async loadDataForCustomization() {
    await this.loadAccountVariables();
    this.loadCustomFonts();
    this.loadDefaultFonts();
    await this.getBrandVariables();
    this.verifyEmailMessageSubscription = this.brandVariablesService.showVerifyEmailMessage.subscribe((showMessage) => {
      this.calcPositionActionsRow(showMessage);
    });
  }

  private async loadAccountVariables(): Promise<void> {
    this.loadingAccountVariables = true;
    return new Promise((resolve, reject) => {
      this.accountSettingService.getAllAccountVariables().subscribe(
        (accountVariables: Array<AccountVariable>) => {
          if (accountVariables) {
            this.allAccountVariables = accountVariables;
            this.accountColors = accountVariables.filter((variable) => variable.type === AccountVariableType.Color);
            this.accountFonts = accountVariables.filter((variable) => variable.type === AccountVariableType.Font);
            this.accountTextVariables = accountVariables.filter(
              (variable) => variable.type === AccountVariableType.Text,
            );
            this.accountTextVariables.forEach((variables) => (variables.saveDisabled = true));
            if (!this.accountTextVariables.length) this.addNewTextFields();

            this.initialAccountVariables = JSON.parse(
              JSON.stringify([...this.accountColors, ...this.accountFonts, ...this.accountTextVariables]),
            );
            this.checkAndUpdateCustomFontFlags();
          } else {
            this.accountColors = [];
            this.accountFonts = [];
            this.accountTextVariables = [];
            this.allAccountVariables = [];
          }
          this.loadingAccountVariables = false;
          resolve();
        },
        (error) => {
          this.loadingAccountVariables = false;
          this.logger.error(
            'Error that should break the page, account variables could not be retrieved: ' + JSON.stringify(error),
          );
          this.showBrokenPageDialog();
          reject(error);
        },
      );
    });
  }

  private checkAndUpdateCustomFontFlags() {
    if (this.accountFonts && this.customFonts) {
      this.accountFonts.forEach((font: AccountVariableFont) => {
        const customFont = this.customFonts.find((f) => f.name.toLowerCase() === font.value.toLowerCase());
        font.isCustom = !!customFont;
        if (font.isCustom) {
          font.fontType = customFont.type;
        } else {
          font.fontType = AccountCustomFontType.Default;
        }
      });
    }
  }

  private loadCustomFonts() {
    this.customFonts = undefined;
    this.loadingCustomFonts = true;
    this.accountSettingService.getAllCustomFonts().subscribe(
      (customFonts: Array<AccountCustomFont>) => {
        this.customFonts = customFonts;
        this.checkAndUpdateCustomFontFlags();
        this.loadingCustomFonts = false;
        this.sanitizedSupportedCssFonts = this.sanitizedSupportedCssFonts.concat(
          this.customFonts
            .filter((f) => !!f.cssLink && f.cssLink.length > 0)
            .map((font) => this.sanitizer.bypassSecurityTrustResourceUrl(font.cssLink)),
        );
        this.uploadedFonts = this.customFonts.filter((f) => !!f.fontFiles && f.fontFiles.length > 0);
        this.injectAllUploadedFonts(this.uploadedFonts);
      },
      (error) => {
        this.loadingCustomFonts = false;
        this.logger.error('Error that should break the page, fonts could not be retrieved: ' + JSON.stringify(error));
        this.showBrokenPageDialog();
      },
    );
  }

  public loadDefaultFonts() {
    const allFonts = WidgetComponentHelpers.getSupportedFontFamilies(this.widgetEditorDataService.customFonts);
    this.defaultFonts = allFonts.filter((font) => font.isDefault === true);
    this.defaultFonts.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    this.sanitizedSupportedCssFonts = this.defaultFonts
      .filter((f) => !!f.cssLink && f.cssLink.length > 0)
      .map((font) => this.sanitizer.bypassSecurityTrustResourceUrl(font.cssLink));
  }

  public deleteAccountVariable(
    accountVariable: AccountVariable | AccountCustomFont,
    variableType: AccountVariableType,
  ): void {
    this.showProgressDialog();
    let accountId;
    if (variableType === AccountVariableType.Font && 'name' in accountVariable) {
      const font = this.accountFonts.find((x) => x.value === accountVariable.name);
      if (font) accountId = font.id;
    } else accountId = accountVariable.id;

    this.accountSettingService.deleteAccountVariable(accountId).subscribe(
      () => {
        if (variableType === AccountVariableType.Color) {
          const colorIndex = this.accountColors.findIndex((color) => color.id === accountId);
          if (colorIndex !== -1) {
            this.accountColors = this.accountColors.filter((_, i) => i !== colorIndex);
            this.lastAddedColor = this.lastAddedColor.filter((color) => color.id !== accountId);
          }
          this.snackbarService.success('settings.customization.colors.delete-success');
        } else if (variableType === AccountVariableType.Text) {
          const textIndex = this.accountTextVariables.findIndex((text) => text.id === accountId);
          if (textIndex !== -1) {
            this.accountTextVariables.splice(textIndex, 1);
          }
          if (!this.accountTextVariables.length) this.addNewTextFields();
          this.snackbarService.success('settings.customization.text-variables.delete-success');
        } else {
          throw Error('Not supported yet');
        }
        this.initialAccountVariables = this.initialAccountVariables.filter((variable) => variable.id !== accountId);
        this.closeProgressDialog();
      },
      (error) => {
        this.snackbarService.error('generic.errorOccurred');
        this.logger.error('Error that should break the page, variable could not be deleted: ' + JSON.stringify(error));
        this.closeProgressDialog();
      },
    );
  }

  public deleteCustomFont(customFontId: string): void {
    this.accountSettingService.deleteCustomFont(customFontId).subscribe(
      () => {
        const fontIndex = this.customFonts.findIndex((font) => font.id === customFontId);
        if (fontIndex !== -1) {
          this.customFonts.splice(fontIndex, 1);
        }
        if (fontIndex !== -1) {
          this.accountFonts.splice(fontIndex, 1);
        }
        this.checkAndUpdateCustomFontFlags();
        this.snackbarService.success('settings.customization.delete-font-success');

        this.closeProgressDialog();
      },
      (error) => {
        this.snackbarService.error('generic.errorOccurred');
        this.logger.error('Error that should break the page, font could not be deleted: ' + JSON.stringify(error));
        this.closeProgressDialog();
      },
    );
  }

  private showBrokenPageDialog() {
    if (!this.brokenPageDialogRef) {
      this.brokenPageDialogRef = this.dialog.open(BrokenPageDialogComponent, {
        panelClass: 'broken-page-dialog-comp',
        disableClose: true,
      });
    }
  }

  private showProgressDialog() {
    this.progressDialogRef = this.dialog.open(IndeterminateProgressDialogComponent, {
      panelClass: 'transparent',
      disableClose: true,
    });
  }

  private closeProgressDialog() {
    if (this.progressDialogRef) {
      this.progressDialogRef.close();
    }
  }

  //Color Variables
  public editAccountColor(updatedColor, proceedAction?): void {
    this.savingColorInProgress = true;
    (this.accountSettingService.updateAccountVariable(updatedColor).subscribe((updatedColor) => {
      this.initialAccountVariables = this.initialAccountVariables.map((obj) =>
        obj.id === updatedColor.id ? updatedColor : obj,
      );
      this.savingColorInProgress = false;
      this.snackbarService.success('settings.customization.colors.update-success');
      if (proceedAction) {
        this.selectedTab = +proceedAction || this.selectedTab;
        this.prevTab = { index: 0, key: TabsBrandSettings.BrandDesign };
      }
    }),
      () => {
        this.savingColorInProgress = false;
        this.snackbarService.error('generic.errorOccurred');
      });
  }

  public createdAccountColor(): void {
    if (
      this.errorKeyColor ||
      this.errorValueColor ||
      !this.newColor ||
      this.newColor?.key === '' ||
      this.newColor?.value === ''
    )
      return;
    this.savingColorInProgress = true;
    this.newColor.value = this.toHexWithAlfa(this.newColor.value);
    this.accountSettingService.createAccountVariable(this.newColor).subscribe(
      (newColor: AccountVariable) => {
        this.initialAccountVariables.push(JSON.parse(JSON.stringify(newColor)));
        this.accountColors = [...this.accountColors, newColor];
        this.lastAddedColor.push(newColor);
        this.newColor = null;
        this.savingColorInProgress = false;
        this.snackbarService.success('settings.customization.colors.create-success');
        this.focusLastColorKey();
      },
      () => {
        this.savingColorInProgress = false;
        this.snackbarService.error('generic.errorOccurred');
      },
    );
  }

  public focusLastColorKey(): void {
    setTimeout(() => {
      const inputsArray = this.inputs.toArray();
      const lastInput = inputsArray[inputsArray.length - 1];
      lastInput?.nativeElement.focus();
    });
  }

  public toHexWithAlfa(color: string) {
    if (color && color.length === 7) {
      return ColorHelpers.getTransparentColor(color, 1);
    }
    return color;
  }

  public inputColorValue(updatedColor): void {
    this.validateColorValue(updatedColor);
    updatedColor.errorValueColor = this.errorValueColor;
  }

  public changeOnColorPicker(color: string, item): void {
    item.value = color;
    this.validateColorValue(item);
  }

  public validateColorKey(color): void {
    color.key = color.key.trim();
    this.errorKeyColor = undefined;
    if (!color.key) {
      this.errorKeyColor = this.translateService.instant('generic.validation.error-required');
    } else if (color.key.length < 3) {
      this.errorKeyColor = this.translateService.instant('generic.validation.error-minlength', { length: 3 });
    } else if (color.key.length > 20) {
      this.errorKeyColor = this.translateService.instant('generic.validation.error-maxlength', { length: 20 });
    } else if (!/^[a-z][a-z\d_\s]+$/i.test(color.key)) {
      this.errorKeyColor = this.translateService.instant('generic.validation.error-invalid-characters');
    } else if (
      this.initialAccountVariables
        .filter((variable) => variable.type === AccountVariableType.Color)
        .some((av) => {
          return av.key.toLowerCase() === color.key.toLowerCase();
        })
    ) {
      this.errorKeyColor = this.translateService.instant('generic.validation.error-item-exists');
    }
  }

  public inputColorKey(color): void {
    this.validateColorKey(color);
    color.errorKeyColor = this.errorKeyColor;
  }

  public validateColorValue(color): void {
    color.value = color.value.trim();
    this.errorValueColor = undefined;
    if (!color.value) {
      this.errorValueColor = this.translateService.instant('generic.validation.error-required');
    } else {
      const isHexColor = ColorHelpers.validateHexColor(color.value);
      let isRgbaColor = false;
      if (!isHexColor) {
        isRgbaColor = ColorHelpers.validateRgbaColor(color.value);
      }
      if (!isHexColor && !isRgbaColor) {
        this.errorValueColor = this.translateService.instant('generic.validation.error-invalid-color-code');
      }
    }
  }

  private injectAllUploadedFonts(uploadedFonts): void {
    if (uploadedFonts != null && uploadedFonts.length > 0) {
      setTimeout(() => {
        uploadedFonts.forEach((uploadedFont: AccountCustomFont) => {
          WidgetComponentHelpers.renderCustomFont(uploadedFont);
        });
      }, 100);
    }
  }

  public closeColorMenu(event) {
    if (event && !event?.target.classList.contains('iconClose')) {
      event.stopPropagation();
    }
  }

  public onMenuColorClosed(updatedColor): void {
    this.inputColorValue(updatedColor);
    this.editAccountColorOnBlur(updatedColor);
  }

  public addNewColorFields(): void {
    this.newColor = {
      key: ColorHelpers.getNextDefaultColorName(this.accountColors),
      value: '#cccccc',
      type: AccountVariableType.Color,
    };
    this.errorKeyColor = '';
    this.errorValueColor = '';
    this.createdAccountColor();
  }

  public deleteNewColorFields(): void {
    this.newColor = null;
  }

  public getInitialVariable(updatedVar): AccountVariable {
    return this.initialAccountVariables.find((variable) => variable.id === updatedVar.id);
  }

  //Text Variables
  public createdAccountText(): void {
    if (
      this.errorKeyText ||
      this.errorValueText ||
      !this.newText ||
      this.newText?.key === '' ||
      this.newText?.value === '' ||
      this.accountTextVariables.length >= 40
    )
      return;
    this.savingTextInProgress = true;
    this.accountSettingService.createAccountVariable(this.newText).subscribe(
      (newText: AccountVariable) => {
        this.initialAccountVariables.push(JSON.parse(JSON.stringify(newText)));
        this.accountTextVariables.push(newText);
        this.newText = null;
        this.savingTextInProgress = false;
        this.snackbarService.success('settings.customization.text-variables.create-success');
      },
      () => {
        this.savingTextInProgress = false;
        this.snackbarService.error('generic.errorOccurred');
      },
    );
  }

  public editAccountText(updatedText): void {
    if (this.getInitialVariable(updatedText).value === updatedText.value || this.errorValueText) return;
    this.savingTextInProgress = true;
    (this.accountSettingService.updateAccountVariable(updatedText).subscribe((updatedText) => {
      this.initialAccountVariables = this.initialAccountVariables.map((obj) =>
        obj.id === updatedText.id ? updatedText : obj,
      );
      this.savingTextInProgress = false;
      this.snackbarService.success('settings.customization.text-variables.update-success');
    }),
      () => {
        this.savingTextInProgress = false;
        this.snackbarService.error('generic.errorOccurred');
      });
  }

  public inputTextValue(updatedText): void {
    this.validateTextValue(updatedText);
    updatedText.errorValueText = this.errorValueText;
    if (this.getInitialVariable(updatedText).value === updatedText.value) {
      updatedText.saveDisabled = true;
    } else updatedText.saveDisabled = false;
  }

  public validateTextKey(text): void {
    text.key = text.key.trim();
    this.errorKeyText = undefined;
    if (!text.key) {
      this.errorKeyText = this.translateService.instant('generic.validation.error-required');
    } else if (text.key.length < 4) {
      this.errorKeyText = this.translateService.instant('generic.validation.error-minlength-4');
    } else if (text.key.length > 20) {
      this.errorKeyText = this.translateService.instant('generic.validation.error-maxlength', { length: 20 });
    } else if (!/^[A-Za-zÅÄÖåäö0-9_.'"]+$/i.test(text.key)) {
      this.errorKeyText = this.translateService.instant('generic.validation.error-invalid-characters');
    } else if (
      this.initialAccountVariables
        .filter((variable) => variable.type === AccountVariableType.Text)
        .some((av) => {
          return av.key.toLowerCase() === text.key.toLowerCase();
        })
    ) {
      this.errorKeyText = this.translateService.instant('generic.validation.error-item-exists');
    }
  }

  public validateTextValue(text): void {
    text.value = text.value?.trim();
    this.errorValueText = undefined;
    if (!text.value || text.value.trim() === '') {
      this.errorValueText = this.translateService.instant('generic.validation.error-required');
    } else if (!this.textValueRegex.test(text.value)) {
      this.errorValueText = this.translateService.instant('generic.validation.error-invalid-characters');
    }
  }

  public addNewTextFields(): void {
    this.newText = {
      key: '',
      value: '',
      type: AccountVariableType.Text,
    };
    this.errorKeyText = '';
    this.errorValueText = '';
  }

  public deleteNewTextFields(): void {
    if (this.accountTextVariables.length) {
      this.newText = null;
    } else this.addNewTextFields();
  }

  public addEditCustomFont(editingFont?): void {
    this.flyoutService.open({
      type: FlyoutDataType.BrandSettings,
      width: 400,
      editingFont: editingFont,
      transparentBackDrop: true,
      existingFonts: this.accountFonts,
      customFonts: this.customFonts,
      allAccountVariables: this.allAccountVariables,
    } as FlyoutOpeningData);
  }

  // Brand variables
  public async getBrandVariables(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.brandVariablesService.getLatest().subscribe((brandVariables) => {
        this.loadingBrandVariables = false;
        if (brandVariables) {
          this.brandVariables = this.modifyFromBrandVariablesObject(brandVariables);
          this.initialBrandVariables = JSON.parse(JSON.stringify(this.brandVariables));
          this.brandVariablesPreview = this.brandVariables;
          this.setPrevTab();
          resolve();
        } else {
          this.loadingBrandVariables = true;
          this.brandVariablesService.getDefaultSettings().subscribe((defaultBrandVariables) => {
            this.loadingBrandVariables = false;
            this.brandVariables = this.modifyFromBrandVariablesObject(defaultBrandVariables);
            this.isAutomaticallyGenerated = defaultBrandVariables.isAutomaticallyGenerated;
            this.initialBrandVariables = null;
            this.brandVarCanBeSavedOnClickTab = { brandVariablesErrors: false, isBrandSettingsNotChanged: false };
            this.brandVariablesPreview = this.brandVariables;
            this.setPrevTab();
            resolve();
          }, reject);
        }
      }, reject);
    });
  }

  public editAccountColorOnBlur(updatedColor, event?) {
    if (this.colorBlurHandled) return;
    this.colorBlurHandled = true;
    clearTimeout(this.colorBlurTimeout);
    this.colorBlurTimeout = setTimeout(() => {
      this.colorBlurHandled = false;
    }, 200);

    let proceedAction = 0;
    if (event) {
      if (event.relatedTarget?.classList.contains('mat-mdc-tab')) {
        const related = event.relatedTarget as HTMLElement | null;
        if (related) {
          const id = related.id;
          proceedAction = +id.charAt(id.length - 1);
        }
      } else if (event.relatedTarget?.classList.contains('next-btn')) {
        proceedAction = 1;
      }
    }

    if (updatedColor) {
      if (
        this.getInitialVariable(updatedColor)?.key !== updatedColor.key &&
        !this.errorValueColor &&
        this.getInitialVariable(updatedColor)?.value === updatedColor.value &&
        !this.errorKeyColor
      ) {
        if (this.lastAddedColor.find((color) => color.id === updatedColor.id)) {
          this.editAccountColor(updatedColor, proceedAction);
        } else {
          this.confirmationDialogService
            .confirm({
              title: 'confirmationDialog.confirmationTitle',
              text: 'settings.customization.colors.warning-change-color-key',
              proceedButtonText: 'generic.update',
              cancelButtonText: 'generic.cancel',
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                this.editAccountColor(updatedColor, proceedAction);
              } else {
                updatedColor.key = this.getInitialVariable(updatedColor)?.key;
                this.selectedTab = proceedAction;
                this.prevTab = { index: 0, key: TabsBrandSettings.BrandDesign };
              }
            });
        }
      }
      if (
        this.getInitialVariable(updatedColor)?.value !== updatedColor.value &&
        !this.errorKeyColor &&
        this.getInitialVariable(updatedColor)?.key === updatedColor.key &&
        !this.errorValueColor
      ) {
        this.savingColorInProgress = true;
        const colorIsRgba = ColorHelpers.validateRgbaColor(updatedColor.value);
        if (colorIsRgba) {
          updatedColor.value = ColorHelpers.rgbaToHex(updatedColor.value);
        }
        updatedColor.value = this.toHexWithAlfa(updatedColor.value);
        this.editAccountColor(updatedColor);
      }
    }
  }

  public editAccountColorOnEnter(color, event: KeyboardEvent): void {
    event.preventDefault();
    this.editAccountColorOnBlur(color, event);
  }

  public saveBrandSettingsByClickOnTab(inputKey): void {
    setTimeout(() => {
      if (this.isBrandVarPageForTemplatePage) return;
      if (
        (this.prevTab?.key === TabsBrandSettings.CampaignDesign ||
          this.prevTab?.key === TabsBrandSettings.FormDesign) &&
        this.brandVarCanBeSavedOnClickTab
      ) {
        let brandVarHasError = this.isBrandVarHasError();
        if (!brandVarHasError && !this.brandVarCanBeSavedOnClickTab?.isBrandSettingsNotChanged) {
          this.saveBrandVariables();
          this.brandVarCanBeSavedOnClickTab.isBrandSettingsNotChanged = true;
        }
      } else {
        if (this.prevTab?.key === TabsBrandSettings.TextVariables) {
          this.createdAccountText();
        }
      }
      this.prevTab = { index: this.selectedTab, key: inputKey };
    }, 0);
  }

  public nextTab(): void {
    const key = this.tabGroup._tabs.get(this.selectedTab || 0)?.textLabel;
    this.saveBrandSettingsByClickOnTab(key);
    this.selectedTab = this.selectedTab ? this.selectedTab + 1 : 1;
  }

  public saveAllBrandSettings(): void {
    this.createdAccountText();
    this.createdAccountColor();
    if (!this.brandVarCanBeSavedOnClickTab && !this.isBrandVarPageForTemplatePage) return;
    let brandVarHasError;
    if (this.brandVarCanBeSavedOnClickTab?.brandVariablesErrors) {
      brandVarHasError = this.isBrandVarHasError();
    }
    if (
      !brandVarHasError &&
      !this.brandVarCanBeSavedOnClickTab?.isBrandSettingsNotChanged &&
      !this.isBrandVarPageForTemplatePage
    ) {
      this.saveBrandVariables();
      this.brandVarCanBeSavedOnClickTab.isBrandSettingsNotChanged = true;
    } else if (!brandVarHasError && this.isBrandVarPageForTemplatePage) {
      this.saveBrandVariables();
    }
  }

  public isBrandVarHasError(): boolean {
    return Object.values(this.brandVarCanBeSavedOnClickTab?.brandVariablesErrors).some((section) =>
      Object.values(section).some((value) => {
        if (
          (typeof value === 'string' && value.length) ||
          (typeof value === 'object' && Object.values(value).some((item) => item))
        ) {
          return true;
        } else return false;
      }),
    );
  }

  public saveBrandVariables(): void {
    this.brandVariables.canvas.backgroundColor = this.toHexWithAlfa(this.brandVariables.canvas.backgroundColor);
    this.brandVariables.canvas.closeButtonColor = this.toHexWithAlfa(this.brandVariables.canvas.closeButtonColor);
    this.brandVariables.headline.color = this.toHexWithAlfa(this.brandVariables.headline.color);
    this.brandVariables.text.color = this.toHexWithAlfa(this.brandVariables.text.color);
    this.brandVariables.button.color = this.toHexWithAlfa(this.brandVariables.button.color);
    if (!this.brandVariables.button.border) {
      this.brandVariables.button.border = {} as BrandVariableBorder;
    }
    this.brandVariables.button.border.color = this.toHexWithAlfa(this.brandVariables.button.border?.color);
    this.brandVariables.button.backgroundColor = this.toHexWithAlfa(this.brandVariables.button.backgroundColor);
    this.brandVariables.label.color = this.toHexWithAlfa(this.brandVariables.label.color);
    this.brandVariables.input.color = this.toHexWithAlfa(this.brandVariables.input.color);
    this.brandVariables.input.border.color = this.toHexWithAlfa(this.brandVariables.input.border.color);
    this.brandVariables.input.backgroundColor = this.toHexWithAlfa(this.brandVariables.input.backgroundColor);

    const objToSend: BrandVariable = {
      headline: this.transformBrandVariable(this.brandVariables.headline),
      text: this.transformBrandVariable(this.brandVariables.text),
      button: {
        ...this.transformBrandVariable(this.brandVariables.button),
        radius: { roundValue: this.brandVariables.button.radius.roundValue + 'px' },
        backgroundColor: this.brandVariables.button.backgroundColor,
        border: {
          color: this.brandVariables.button.border?.color,
          width: this.brandVariables.button.border?.width ? this.brandVariables.button.border?.width + 'px' : null,
        },
        padding: this.brandVariables.button.padding,
        margin: this.brandVariables.button.margin,
        height: this.brandVariables.button.height ? this.brandVariables.button.height + 'px' : null,
        alignment: this.brandVariables.button.alignment,
      },
      input: {
        ...this.transformBrandVariable(this.brandVariables.input),
        radius: { roundValue: this.brandVariables.input.radius.roundValue + 'px' },
        backgroundColor: this.brandVariables.input.backgroundColor,
        border: {
          color: this.brandVariables.input.border?.color,
          width: this.brandVariables.input.border?.width ? this.brandVariables.input.border?.width + 'px' : null,
        },
        padding: this.brandVariables.input.padding,
        margin: this.brandVariables.input.margin,
        height: this.brandVariables.input.height ? this.brandVariables.input.height + 'px' : null,
      },
      label: this.transformBrandVariable(this.brandVariables.label),
      canvas: {
        backgroundColor: this.brandVariables.canvas.backgroundColor,
        closeButtonColor: this.brandVariables.canvas.closeButtonColor,
        radius: { roundValue: this.brandVariables.canvas.radius.roundValue + 'px' },
      },
    };
    (this.brandVariablesService.create(objToSend).subscribe((brandVariables: BrandVariable) => {
      if (brandVariables) {
        ApplicationStarterService.triggerEvent('onTriggerbeeClientBrandSettingsSaved', { id: brandVariables.id });
        this.brandVariables = this.modifyFromBrandVariablesObject(brandVariables);
        this.initialBrandVariables = JSON.parse(JSON.stringify(this.brandVariables));
        this.snackbarService.success('settings.customization.brand-settings.success-save');
        this.isAutomaticallyGenerated = false;
        this.onChangeBrandVariables.emit(false);
        if (this.isBrandVarPageForTemplatePage) {
          this.onSaveBrandVariables.emit();
        }
      }
    }),
      () => {
        this.snackbarService.error('generic.errorOccurred');
      });
  }

  public skipBrandSettings(): void {
    this.onSaveBrandVariables.emit();
    this.cookieService.set('notShowBrandVarPageOnTemplatePage', true, 7, true);
  }

  private transformBrandVariable(variable: BrandVariableTextProperties): BrandVariableTextProperties {
    return {
      font: variable.textProperties.font,
      fontSize: variable.textProperties.fontSize,
      fontWeight: variable.textProperties.fontWeight,
      letterSpacing: variable.textProperties.letterSpacing,
      lineHeight: variable.textProperties.lineHeight,
      color: variable.color,
    };
  }

  private modifyFromBrandVariablesObject(brandVariables: BrandVariable): BrandVariable {
    return {
      text: {
        textProperties: this.transformFromBrandVariable(brandVariables.text),
        color: brandVariables.text.color,
      },
      headline: {
        textProperties: this.transformFromBrandVariable(brandVariables.headline),
        color: brandVariables.headline.color,
      },
      button: {
        textProperties: this.transformFromBrandVariable(brandVariables.button),
        color: brandVariables.button.color,
        radius: {
          roundValue: this.extractNumberFromPixelString(brandVariables.button.radius.roundValue),
        },
        backgroundColor: brandVariables.button.backgroundColor,
        border: {
          color: brandVariables.button.border?.color,
          width: this.extractNumberFromPixelString(brandVariables.button.border?.width),
        },
        padding: brandVariables.button.padding,
        margin: brandVariables.button.margin,
        height: this.extractNumberFromPixelString(brandVariables.button.height),
        alignment: brandVariables.button.alignment,
      },
      input: {
        textProperties: this.transformFromBrandVariable(brandVariables.input),
        color: brandVariables.input.color,
        radius: {
          roundValue: this.extractNumberFromPixelString(brandVariables.input.radius?.roundValue),
        },
        backgroundColor: brandVariables.input.backgroundColor,
        border: {
          color: brandVariables.input.border?.color,
          width: this.extractNumberFromPixelString(brandVariables.input.border?.width),
        },
        padding: brandVariables.input.padding,
        margin: brandVariables.input.margin,
        height: this.extractNumberFromPixelString(brandVariables.input.height),
      },
      label: {
        textProperties: this.transformFromBrandVariable(brandVariables.label),
        color: brandVariables.label.color,
      },
      canvas: {
        backgroundColor: brandVariables.canvas.backgroundColor,
        closeButtonColor: brandVariables.canvas.closeButtonColor,
        radius: {
          roundValue: this.extractNumberFromPixelString(brandVariables.canvas.radius?.roundValue),
        },
      },
    };
  }

  private extractNumberFromPixelString(value: string | number): string {
    return WidgetComponentHelpers.extractNumberFromPixelString(value)?.toString() ?? '0';
  }

  private transformFromBrandVariable(variable) {
    return {
      font: variable.font,
      fontSize: variable.fontSize,
      fontWeight: variable.fontWeight,
      letterSpacing: variable.letterSpacing,
      lineHeight: variable.lineHeight,
    };
  }

  private setPrevTab() {
    this.selectedTab = 0;
    if (this.isBrandVarPageForTemplatePage) {
      this.prevTab = { index: 0, key: TabsBrandSettings.CampaignDesign };
    } else {
      this.prevTab = { index: 0, key: TabsBrandSettings.BrandDesign };
    }
  }

  private calcPositionActionsRow(showMessage): void {
    setTimeout(() => {
      let bunner = document.getElementsByClassName('verify-email-message-box')[0];
      let bvActionsRow = document.getElementsByClassName('bv-actions-row')[0];
      let heightBunner = (<HTMLElement>bunner)?.offsetHeight;
      if (bvActionsRow && showMessage) {
        let comp = bvActionsRow as HTMLElement;
        comp.style.bottom = heightBunner + 'px';
      } else if (bvActionsRow && showMessage === false) {
        let comp = bvActionsRow as HTMLElement;
        comp.style.bottom = '0px';
      }
    }, 100);
  }

  public onPreviewRefresh(event): void {
    this.brandVariablesPreview = JSON.parse(JSON.stringify(event));
  }

  public deleteFont(font: AccountCustomFont): void {
    this.deleteCustomFont(font.id);
  }

  public openCopyBrandSettings(): void {
    this.flyoutService.open({
      type: FlyoutDataType.CopyAccountSettings,
      width: 500,
    } as FlyoutOpeningData);
  }

  ngOnDestroy() {
    if (this.flyoutSubscription) this.flyoutSubscription.unsubscribe();
    if (this.verifyEmailMessageSubscription) this.verifyEmailMessageSubscription.unsubscribe();
  }
}

export enum TabsBrandSettings {
  CampaignDesign = 'campaignDesign',
  BrandDesign = 'brandDesign',
  FormDesign = 'formDesign',
  Fonts = 'fonts',
  ColorPalette = 'colorPalette',
  TextVariables = 'textVariables',
}
